import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate } from "@/lib/router-compat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";

type CtaContextType = {
  openConsultation: (source?: string) => void;
};

const CtaContext = createContext<CtaContextType | null>(null);

export function createConsultationModal() {
  const ConsultationModalProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [source, setSource] = useState<string>("consultation");

    const openConsultation = useCallback((src?: string) => {
      setSource(src ?? "consultation");
      setOpen(true);
    }, []);

    return (
      <CtaContext.Provider value={{ openConsultation }}>
        {children}
        <ConsultationModal open={open} onOpenChange={setOpen} source={source} />
      </CtaContext.Provider>
    );
  };

  const useConsultationModal = () => {
    const ctx = useContext(CtaContext);
    if (!ctx) {
      throw new Error(
        "useConsultationModal must be used within ConsultationModalProvider",
      );
    }
    return ctx;
  };

  return { ConsultationModalProvider, useConsultationModal };
}

const fieldClass =
  "w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/30";

function ConsultationModal({
  open,
  onOpenChange,
  source,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: string;
}) {
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const firstName = String(data.get("first_name") || "");
    const lastName = String(data.get("last_name") || "");
    const email = String(data.get("email") || "");
    const phone = String(data.get("phone") || "");
    const insurance = String(data.get("insurance") || "");
    const message = String(data.get("message") || "");

    const payload = buildTrackingPayload(
      source || "consultation-modal",
      "Consultation Request",
    );
    payload.formData = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      calendar_notes: message
        ? `${message}${insurance ? ` | Pathway: ${insurance}` : ""}`
        : insurance
          ? `Pathway: ${insurance}`
          : "",
    };
    payload.formLabels = {
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      calendar_notes: "Message",
    };

    postTrackingEvent(payload, {
      customFields: {
        yWO5ZTXyq5IpOcNsYrIz: {
          value: insurance,
          label: "Access pathway",
        },
      },
    });

    trackLead(source || "consultation-modal", "Consultation Request");
    trackLeadConversion(source || "consultation-modal", "Consultation Request");
    onOpenChange(false);
    form.reset();
    navigate("/thank-you-consultation");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto p-0 sm:rounded-2xl">
        <DialogHeader className="bg-gradient-navy px-6 py-5 text-center">
          <DialogTitle className="font-headings text-xl font-bold text-white">
            Request a Free Consultation
          </DialogTitle>
          <DialogDescription className="text-white/80">
            We respond within one business day. Your initial consultation is a
            convenient Zoom call.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="cm-first"
                className="mb-1 block text-xs font-semibold text-primary"
              >
                First Name *
              </label>
              <input
                id="cm-first"
                name="first_name"
                required
                className={fieldClass}
                placeholder="Jane"
              />
            </div>
            <div>
              <label
                htmlFor="cm-last"
                className="mb-1 block text-xs font-semibold text-primary"
              >
                Last Name *
              </label>
              <input
                id="cm-last"
                name="last_name"
                required
                className={fieldClass}
                placeholder="Doe"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="cm-email"
              className="mb-1 block text-xs font-semibold text-primary"
            >
              Email *
            </label>
            <input
              id="cm-email"
              name="email"
              type="email"
              required
              className={fieldClass}
              placeholder="jane@email.com"
            />
          </div>
          <div>
            <label
              htmlFor="cm-phone"
              className="mb-1 block text-xs font-semibold text-primary"
            >
              Phone *
            </label>
            <input
              id="cm-phone"
              name="phone"
              type="tel"
              required
              className={fieldClass}
              placeholder="(954) 000-0000"
            />
          </div>
          <div>
            <label
              htmlFor="cm-insurance"
              className="mb-1 block text-xs font-semibold text-primary"
            >
              Insurance / Self-Pay
            </label>
            <select
              id="cm-insurance"
              name="insurance"
              className={fieldClass}
              defaultValue=""
            >
              <option value="" disabled>
                Select…
              </option>
              <option value="self-pay">Self-Pay (fastest)</option>
              <option value="bcbs">Blue Cross Blue Shield</option>
              <option value="bcbs-federal">
                BCBS Federal Employee Program
              </option>
              <option value="medicare">Medicare</option>
              <option value="other">Other / Not sure</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="cm-message"
              className="mb-1 block text-xs font-semibold text-primary"
            >
              Tell us briefly what you're experiencing (optional)
            </label>
            <textarea
              id="cm-message"
              name="message"
              rows={3}
              className={fieldClass}
              placeholder="e.g. I've tried 2 antidepressants without relief…"
            />
          </div>
          <button
            type="submit"
            className="w-full rounded-lg bg-gradient-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-90"
          >
            Request My Free Consultation
          </button>
          <p className="text-center text-[0.7rem] text-muted-foreground">
            By submitting, you agree to be contacted by Neuro Wellness TMS
            Centers of America. If you are in crisis, call or text 988.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
